import { useContext } from "react";
import { AuthContext } from "../auth-context";
import { tryOrMessage } from "../utils/strings";
import { ImgSideLeft, ImgSideRight } from "../widget/images";
import IOBoolean from "../widget/io-boolean";
import IOListNumber from "../widget/io-list-number";
import IONumber from "../widget/io-number";
import IOPanelWithNavigation from "../widget/io-panel-with-navigation";
import Panel from "../widget/panel";
import TwoColumns from "../widget/two-columns";
import type { ConsultClubfoot, Patient } from "./_objects-patient";
import ConsultAbstractConclusion from "./blocs/consult-abstract-conclusion";
import ConsultAbstractIntroduction from "./blocs/consult-abstract-introduction";
import { type RelatedElementGeneratorProps } from "./patient-related-element-generator";

import IOString from "../widget/io-string";
function ifNull0(value: number | null | undefined) {
  return value ?? 0;
}

function getPiraniLeft(file: ConsultClubfoot) {
  try {
    return (
      ifNull0(file.curved_lateral_border_left) +
      ifNull0(file.medial_crease_left) +
      ifNull0(file.talar_head_coverage_left) +
      ifNull0(file.posterior_crease_left) +
      ifNull0(file.rigid_equinus_left) +
      ifNull0(file.empty_heel_left)
    );
  } catch (e) {
    throw new Error("?", { cause: e });
  }
}

function getPiraniRight(file: ConsultClubfoot) {
  try {
    return (
      ifNull0(file.curved_lateral_border_right) +
      ifNull0(file.medial_crease_right) +
      ifNull0(file.talar_head_coverage_right) +
      ifNull0(file.posterior_crease_right) +
      ifNull0(file.rigid_equinus_right) +
      ifNull0(file.empty_heel_right)
    );
  } catch (e) {
    throw new Error("?", { cause: e });
  }
}

export default function ConsultClubfootElement(
  props: { patient: Patient } & RelatedElementGeneratorProps<ConsultClubfoot>
): React.ReactNode {
  const authenticationContext = useContext(AuthContext)!;

  return (
    <IOPanelWithNavigation<ConsultClubfoot>
      {...props}
      key={`consult_clubfoot.${props.file.id}`}
      type="consult_clubfoot"
      apiRootUrl={`/api/patient/${props.patient.id}/consult_clubfoot`}
      restrictedForWrite="write"
      restrictedForUnlock="manage_archives"
      canBeLocked={true}
      basePath={`${props.parentPath}/consult_clubfoot`}
      header={
        <>
          <span className="with-image">
            <ImgSideRight />
            {getPiraniRight(props.file) as number}
          </span>
          <span className="with-image">
            <ImgSideLeft />
            {getPiraniLeft(props.file) as number}
          </span>
        </>
      }
    >
      <ConsultAbstractIntroduction file={props.file} patient={props.patient} />
      <TwoColumns>
        <Panel
          fixed
          header={
            <>
              <ImgSideRight />
              <span>Pirani Right (&lt; 3 years)</span>
              {tryOrMessage(() => getPiraniRight(props.file))}
            </>
          }
        >
          <div className="separator">Mid Foot</div>
          <IOListNumber
            name="curved_lateral_border_right"
            value={props.file.curved_lateral_border_right}
            list={authenticationContext.getList("Pirani")}
          />
          <IOListNumber
            name="medial_crease_right"
            value={props.file.medial_crease_right}
            list={authenticationContext.getList("Pirani")}
          />
          <IOListNumber
            name="talar_head_coverage_right"
            value={props.file.talar_head_coverage_right}
            list={authenticationContext.getList("Pirani")}
          />
          <div className="separator">Hind Foot</div>
          <IOListNumber
            name="posterior_crease_right"
            value={props.file.posterior_crease_right}
            list={authenticationContext.getList("Pirani")}
          />
          <IOListNumber
            name="rigid_equinus_right"
            value={props.file.rigid_equinus_right}
            list={authenticationContext.getList("Pirani")}
          />
          <IOListNumber
            name="empty_heel_right"
            value={props.file.empty_heel_right}
            list={authenticationContext.getList("Pirani")}
          />
          <div className="separator">Score</div>
          <IOString
            label="Score"
            value={tryOrMessage(() => getPiraniRight(props.file) + "")}
          />
        </Panel>
        <Panel
          fixed
          header={
            <>
              <ImgSideLeft />
              <span>Pirani Left (&lt; 3 years)</span>
              {tryOrMessage(() => getPiraniLeft(props.file), "")}
            </>
          }
        >
          <div className="separator">Mid Foot</div>
          <IOListNumber
            name="curved_lateral_border_left"
            value={props.file.curved_lateral_border_left}
            list={authenticationContext.getList("Pirani")}
          />
          <IOListNumber
            name="medial_crease_left"
            value={props.file.medial_crease_left}
            list={authenticationContext.getList("Pirani")}
          />
          <IOListNumber
            name="talar_head_coverage_left"
            value={props.file.talar_head_coverage_left}
            list={authenticationContext.getList("Pirani")}
          />
          <div className="separator">Hind Foot</div>
          <IOListNumber
            name="posterior_crease_left"
            value={props.file.posterior_crease_left}
            list={authenticationContext.getList("Pirani")}
          />
          <IOListNumber
            name="rigid_equinus_left"
            value={props.file.rigid_equinus_left}
            list={authenticationContext.getList("Pirani")}
          />
          <IOListNumber
            name="empty_heel_left"
            value={props.file.empty_heel_left}
            list={authenticationContext.getList("Pirani")}
          />
          <div className="separator">Score</div>
          <IOString
            label="Score"
            value={tryOrMessage(() => getPiraniLeft(props.file) + "", "")}
          />
        </Panel>
      </TwoColumns>
      <TwoColumns>
        <Panel
          fixed
          header={
            <>
              <ImgSideRight />
              <span>Pirani Right (&gt; 3 years)</span>
            </>
          }
        >
          <IOListNumber
            label="Pain"
            value={props.file.pain_right}
            list={authenticationContext.getList("Eval02")}
          />
          <IOListNumber
            name="walking_floor_contact_right"
            value={props.file.walking_floor_contact_right}
            list={authenticationContext.getList("Eval02")}
          />
          <IOListNumber
            name="walking_first_contact_right"
            value={props.file.walking_first_contact_right}
            list={authenticationContext.getList("Eval02")}
          />
          <IOListNumber
            name="jumping_one_leg_right"
            value={props.file.jumping_one_leg_right}
            list={authenticationContext.getList("Eval02")}
          />
          <IOListNumber
            name="run_right"
            value={props.file.run_right}
            list={authenticationContext.getList("Eval02")}
          />
          <IONumber
            name="adduction_angle_right"
            value={props.file.adduction_angle_right}
          />
          <IONumber
            name="hind_foot_angle_w_right"
            value={props.file.hind_foot_angle_w_right}
          />
          <IONumber
            name="dorsal_flexion_max_right"
            value={props.file.dorsal_flexion_max_right}
          />
          <IONumber
            name="plantar_flexion_max_right"
            value={props.file.plantar_flexion_max_right}
          />
          <IOBoolean
            name="muscular_inbalance_right"
            value={props.file.muscular_inbalance_right}
          />
        </Panel>

        <Panel
          fixed
          header={
            <>
              <ImgSideLeft />
              <span>Pirani Left (&gt; 3 years)</span>
            </>
          }
        >
          <IOListNumber
            label="Pain"
            value={props.file.pain_left}
            list={authenticationContext.getList("Pain")}
          />
          <IOListNumber
            name="walking_floor_contact_left"
            value={props.file.walking_floor_contact_left}
            list={authenticationContext.getList("Eval02")}
          />
          <IOListNumber
            name="walking_first_contact_left"
            value={props.file.walking_first_contact_left}
            list={authenticationContext.getList("Eval02")}
          />
          <IOListNumber
            name="jumping_one_leg_left"
            value={props.file.jumping_one_leg_left}
            list={authenticationContext.getList("Eval02")}
          />
          <IOListNumber
            name="run_left"
            value={props.file.run_left}
            list={authenticationContext.getList("Eval02")}
          />
          <IONumber
            name="adduction_angle_left"
            value={props.file.adduction_angle_left}
          />
          <IONumber
            name="hind_foot_angle_w_left"
            value={props.file.hind_foot_angle_w_left}
          />
          <IONumber
            name="dorsal_flexion_max_left"
            value={props.file.dorsal_flexion_max_left}
          />
          <IONumber
            name="plantar_flexion_max_left"
            value={props.file.plantar_flexion_max_left}
          />
          <IOBoolean
            name="muscular_inbalance_left"
            value={props.file.muscular_inbalance_left}
          />
        </Panel>
      </TwoColumns>
      <ConsultAbstractConclusion file={props.file} />
    </IOPanelWithNavigation>
  );
}
